import { useState } from "react";
import { BellRing, Download, Share, SquarePlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { isIOS, isStandalone, promptInstall, useInstall } from "@/lib/pwa";
import { enablePush, pushSupported } from "@/lib/push";
import { cn } from "@/lib/utils";

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-600 text-sm font-bold text-white">{n}</span>
    <span className="pt-0.5 text-[15px] text-slate-700">{children} {Icon && <Icon className="inline h-4 w-4 align-[-2px] text-teal-700" />}</span></li>
);

export const IosInstallSheet = ({ open, onOpenChange }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="bottom" className="pb-safe rounded-t-2xl" data-testid="ios-install-sheet">
      <SheetTitle>Add to Home Screen</SheetTitle>
      <ol className="mt-4 space-y-4">
        <Step n={1} icon={Share}>Tap the Share button in Safari</Step>
        <Step n={2} icon={SquarePlus}>Choose “Add to Home Screen”, then Add</Step>
        <Step n={3}>Open the app from your Home Screen to turn on notifications</Step>
      </ol>
    </SheetContent>
  </Sheet>
);

export const InstallButton = ({ dark, className }) => {
  const mode = useInstall();
  const [ios, setIos] = useState(false);
  if (!mode) return null;
  const click = () => (mode === "prompt" ? promptInstall() : setIos(true));
  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={click} data-testid="install-app-button"
        className={cn("gap-1.5", dark && "border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white", className)}>
        <Download className="h-4 w-4" /> <span>Install app</span>
      </Button>
      <IosInstallSheet open={ios} onOpenChange={setIos} />
    </>
  );
};

/** "Enable notifications" (user gesture). On iOS it's only possible once the app is installed to the Home Screen. */
export const PushPrompt = ({ className }) => {
  const [ios, setIos] = useState(false);
  if (isIOS() && !isStandalone()) return (
    <>
      <button type="button" onClick={() => setIos(true)} className={cn("flex w-full items-center gap-2 bg-amber-50 px-4 py-2.5 text-left text-xs font-medium text-amber-900", className)} data-testid="push-ios-hint">
        <BellRing className="h-4 w-4 shrink-0" /> To get notifications on iPhone or iPad, add this app to your Home Screen first
      </button>
      <IosInstallSheet open={ios} onOpenChange={setIos} />
    </>
  );
  if (!pushSupported() || Notification.permission !== "default") return null;
  const ask = () => enablePush().then(() => toast.success("Notifications are on for this device")).catch((e) => toast.error(e.message));
  return (
    <button type="button" onClick={ask} className={cn("flex w-full items-center gap-2 bg-teal-50 px-4 py-2.5 text-left text-xs font-medium text-teal-800 hover:bg-teal-100", className)} data-testid="notification-enable-push">
      <BellRing className="h-4 w-4 shrink-0" /> Enable notifications on this device
    </button>
  );
};
